import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Event } from '../types';
import { apiService } from '../services/api';

const UpcomingEvents: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await apiService.getEvents();
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      }
    };

    fetchEvents();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCategoryColor = (category: Event['category']) => {
    const colors = {
      mass: 'bg-purple-100 text-purple-800 border-purple-200',
      ministry: 'bg-blue-100 text-blue-800 border-blue-200',
      social: 'bg-green-100 text-green-800 border-green-200',
      education: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      special: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[category];
  };

  return (
    <section className="py-16 bg-gray-50" aria-labelledby="events-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 id="events-heading" className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Upcoming Events
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Stay connected with our parish community through these upcoming events and activities.
          </p>
          <Link
            to="/calendar"
            className="inline-flex items-center bg-church-blue hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
          >
            <CalendarDaysIcon className="h-5 w-5 mr-2" />
            View Full Calendar
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {Array.isArray(events) && events.slice(0, 4).map((event) => (
            <article
              key={event.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-semibold text-church-dark">
                    {event.title}
                  </h3>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full border ${getCategoryColor(event.category)}`}
                  >
                    {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                  </span>
                </div>

                <p className="text-gray-600 mb-4">
                  {event.description}
                </p>

                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="h-4 w-4 mr-2 text-church-blue" />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2 text-church-blue" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-2 text-church-blue" />
                    <span>{event.location}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {events.length > 4 && (
          <div className="text-center">
            <Link
              to="/calendar"
              className="text-church-blue hover:text-blue-800 font-semibold focus:outline-none focus:ring-2 focus:ring-church-blue rounded px-2 py-1"
            >
              View All {events.length} Events →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingEvents;